/* ══════════════════════════════════════════════════════════════════════════
 * Logo generator — selectable name fonts
 *
 * Single source of truth for the "Skrifttype til navne" picker: the option
 * list, the Canvas2D font stacks and the FontFace loading. Both the page
 * (LogoGenerator.jsx) and the canvas engine (logoCanvas.js) read from here —
 * keep it that way. The reference implementation kept two copies of this
 * table and they drifted.
 *
 * `scale` normalises perceived text size across faces. The engine's base
 * sizes (136px school line / 80px names) were tuned for Kaushan, so Kaushan
 * sits at 1.00 and every other face is scaled by the geometric mean of its
 * x-height and cap-height ratio against Kaushan. Purely a visual knob —
 * adjust freely if a face reads too large or too small on the canvas.
 *
 * All faces are self-hosted: the static distribution build (dist-apidemia)
 * runs from file:// and cannot reach a font CDN. Licences live alongside the
 * files in src/assets/cls-dk/logo-generator/fonts/.
 * ══════════════════════════════════════════════════════════════════════════ */

import SatisfyFont from '../../../../assets/cls-dk/logo-generator/fonts/Satisfy-Regular.ttf';
import DancingScriptFont from '../../../../assets/cls-dk/logo-generator/fonts/DancingScript-Variable.ttf';
import KaushanFont from '../../../../assets/cls-dk/logo-generator/fonts/KaushanScript-Regular.ttf';
import PacificoFont from '../../../../assets/cls-dk/logo-generator/fonts/Pacifico-Regular.ttf';

/** Quote a family name for use in a CSS font shorthand. */
const cssFamily = (family) => (/\s/.test(family) ? `'${family}'` : family);

/**
 * `file: null` → system face, nothing to download.
 * `weight` → FontFace descriptor; Dancing Script ships as a variable font.
 */
const FONT_DEFINITIONS = [
  { id: 'satisfy', label: 'Flydende', family: 'Satisfy', fallback: 'cursive', file: SatisfyFont, scale: 1.03 },
  { id: 'moderne', label: 'Enkel', family: 'Arial', fallback: 'sans-serif', file: null, scale: 0.93 },
  { id: 'klassisk', label: 'Tidløs', family: 'Georgia', fallback: 'serif', file: null, scale: 0.98 },
  {
    id: 'dancing',
    label: 'Elegant',
    family: 'Dancing Script',
    fallback: 'cursive',
    file: DancingScriptFont,
    weight: '400 700',
    scale: 1.16,
  },
  { id: 'kaushan', label: 'Legende', family: 'Kaushan', fallback: 'cursive', file: KaushanFont, scale: 1 },
  { id: 'pacifico', label: 'Afslappet', family: 'Pacifico', fallback: 'cursive', file: PacificoFont, scale: 0.91 },
];

export const NAME_FONTS = FONT_DEFINITIONS.map((font) => ({
  ...font,
  /** Canvas2D / CSS font-family stack, e.g. `'Dancing Script', cursive` */
  stack: `${cssFamily(font.family)}, ${font.fallback}`,
}));

export const DEFAULT_NAME_FONT = 'satisfy';

export const getNameFont = (id) => NAME_FONTS.find((font) => font.id === id) || NAME_FONTS[0];

/* ── Loading ──────────────────────────────────────────────────────────────
 * Canvas2D silently falls back to a default face for any family that is not
 * yet in `document.fonts`, so every face must be resolved BEFORE the first
 * draw — not just the selected one, or switching fonts would race.
 * Idempotent: repeated calls share one promise so a remount cannot register
 * duplicate FontFaces.
 * ──────────────────────────────────────────────────────────────────────── */

/** Size used only to warm the canvas font cache; any size resolves the face. */
const WARMUP_SIZE = 80;

let loadPromise = null;

async function registerFonts() {
  if (typeof document === 'undefined' || !document.fonts || typeof FontFace === 'undefined') {
    return;
  }

  await Promise.all(
    NAME_FONTS.filter((font) => font.file).map(async (font) => {
      try {
        const face = new FontFace(font.family, `url(${font.file})`, {
          weight: font.weight || '400',
          style: 'normal',
        });
        await face.load();
        document.fonts.add(face);
      } catch (e) {
        // Non-fatal — the canvas falls back to the stack's generic family.
        console.warn(`Logo generator: font "${font.family}" failed to load`, e);
      }
    })
  );

  await Promise.all(
    NAME_FONTS.map((font) =>
      document.fonts.load(`${WARMUP_SIZE}px ${cssFamily(font.family)}`).catch(() => {})
    )
  );
}

export function loadLogoFonts() {
  if (!loadPromise) {
    loadPromise = registerFonts();
  }
  return loadPromise;
}
